// Hooks
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTitle } from "../../../../Shared/useTitle";

// Styles
import Styles from "./appInfo.module.scss";
import { primaryButtonStyle } from "../../../../Shared/buttonStyles";
import { ACCENTCOLOR } from "../../../../Shared/colors";

// Components
import Loading from "../../../../Shared/loading/loading";
import AddServiceModal from "../addServices-pop-up/addServicesModal";
import Toast from "../../../../Shared/toast/toast";
import DeleteApiKey from "../delete-api-key/deleteApiKey";
import RenderTooltip from "../../../../Shared/tooltip/tooltip";
import ZpinMainPopup from "../../../../Shared/zpin-main-popup/zpinMainPopup";

import Switch from "../../../../Shared/toggle-switch/switch";

// constants
import Info from "./info-message/info";
import EmptyService from "../../../../assets/images/noServicesSubscribe.svg";
import Delete from "../../../../Shared/svg/delete";
import DeleteApp from "../delete-app/deleteApp";
import AddApiKey from "../add-api-key/addApiKey";
import { App, AppKeyStatus } from "../../../../constants/app";

// Constants
import emptyStateKey from "../../../../assets/images/noApiKeyCreated.svg";
import { ReactComponent as CopySvg } from "../../../../assets/images/copy.svg";

// Funtcions
import { callApi } from "../../../../api/fetch";
import { sendAuthOtp, OTP_PURPOSE } from "../../../../utils/otpApi";
import { getCurrentUserId } from "../../../../utils/authSession";
import {
  buildProductNameMap,
  ensureArray,
  formatApiDate,
  isZpinOrOtpError,
  mergeAppWithKeys,
  normalizeProductAccess,
  productPID,
  replaceProductIdsWithNames,
  unwrapListData,
} from "../../../../utils/dashboardMappers";
import { buttonStyles, StyledButton } from "../../../../Shared/button/button";
import DataTable from "../../../../Shared/DataTable/DataTable";
import { testIdProps } from "../../../../utils/testIdProps";

function AppInfo() {
  //  Forbidden services for Product Level settings
  const forbiddenServices = ["merchantreports", "okyc", "rcfinance"];
  const navigate = useNavigate();
  const history = useNavigate();
  const orgId = localStorage.getItem("organisation_id");
  const { appId }: any = useParams();
  const [app, setApp] = useState<null | App>();
  const [toggleDeleteAppAuthPopUp, setToggleDeleteAppAuthPopUp] =
    useState(false);
  const [
    toggleOtpVerificationForAddServiceToApp,
    setToggleOtpVerificationForAddServiceToApp,
  ] = useState(false);
  const [
    toggleOtpVerificationForDisableAPIKey,
    setToggleOtpVerificationForDisableAPIKey,
  ] = useState(false);
  const [
    toggleOtpVerificationForEnableAPIKey,
    setToggleOtpVerificationForEnableAPIKey,
  ] = useState(false);
  const [relayId, setRelayId] = useState("");
  const [otpError, setOtpError] = useState<string | null>(null);
  const [updatedServices, setUpdatedServices] = useState([]);
  const [toggleDeleteKeyPopUp, setToggleDeleteKeyPopUp] = useState(false);
  const [currentKeyName, setCurrentKeyName] = useState<any>("");
  const [toggleAddAppKey, setToggleAddAppKey] = useState(false);
  const [showAddService, setShowAddService] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);
  const [apiKeyCardLoading, setApiKeyCardLoading] = useState(false);
  const [otpVerificationLoading, setOtpVerificationLoading] =
    useState(false);
  const [zpinExists, setZpinExists] = useState(false);
  const [zpinCheckComplete, setZpinCheckComplete] = useState(false);
  const [toggleResetOtp, setToggleResetOtp] = useState(false);
  const [copiedAppId, setCopiedAppId] = useState(false);
  const app_message = {
    TEST: "Integration with this app can only be used for testing products and will deduct trial credits.",
    LIVE: "Integration with this app will be done on live environment and incur wallet balance deductions.",
  };

  const fetchProductNameMap = async () => {
    const fetchList = async (path: string) => {
      try {
        return ensureArray(await callApi(path, { method: "GET" }));
      } catch {
        // Product catalog is optional enrichment; app details should still load
        // when billing/product routes are unavailable (503) or misconfigured.
        return [];
      }
    };

    const [subscribedProducts] = await Promise.all([
      fetchList("/products?subscription_status=subscribed"),
    ]);

    return buildProductNameMap(
      subscribedProducts,
    );
  };

  const fetchAppWithKeys = async (id: string) => {
    const appData = await callApi(`/apps/${id}`, { method: "GET" });
    const keysData = await callApi(
      `/keys?app_ids=${encodeURIComponent(id)}`,
      { method: "GET" }
    );
    // Resolve product_access IDs to names; the app payload only carries IDs.
    const productNameById = await fetchProductNameMap();
    const merged = mergeAppWithKeys(
      appData as Record<string, unknown>,
      unwrapListData<Record<string, unknown>>(keysData, "keys")
    );
    return {
      ...merged,
      products_access: normalizeProductAccess(
        merged.products_access,
        productNameById
      ),
    };
  };

  const updateKeyStatus = async (
    keyName: string,
    status: AppKeyStatus,
    stepUp?: { zpin?: string; otp?: string; relay_id?: string }
  ) => {
    const currentApp = await fetchAppWithKeys(appId);
    const key = currentApp.keys.find(
      (item: Record<string, unknown>) => item.key_name === keyName
    );
    if (!key?.key_id) throw new Error("API key not found");
    const payload: Record<string, string> = {
      status,
      updated_by: getCurrentUserId(),
    };
    if (stepUp?.zpin) payload.zpin = stepUp.zpin;
    if (stepUp?.otp) {
      payload.otp = stepUp.otp;
      payload.relay_id = stepUp.relay_id || relayId;
    }
    await callApi(`/keys/${key.key_id}`, {
      method: "PUT",
      data: payload,
    });
    return fetchAppWithKeys(appId);
  };

  useEffect(() => {
    async function checkForZpin() {
      try {
        const userId = getCurrentUserId();
        if (!userId) return;
        const data = await callApi(
          `/zpins?user_pid=${encodeURIComponent(userId)}`,
          { method: "GET" }
        );
        const zpins = unwrapListData<Record<string, unknown>>(data, "zpins");
        if (zpins.length > 0) setZpinExists(true);
      } catch (error) {
        setZpinExists(false);
      } finally {
        setZpinCheckComplete(true);
      }
    }
    checkForZpin();
  }, [orgId]);

  const loadApp = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchAppWithKeys(appId);
      response.products_access = response?.products_access?.filter(
        (products_access: any) =>
          !forbiddenServices.includes(
            products_access?.name?.replaceAll(" ", "")?.toLowerCase()
          )
      );

      setApp(response as unknown as App);
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      Toast("error", err.message, "Error");
      history("/application/manage-apps");
    }
  }, [appId, history]);

  useEffect(() => {
    loadApp();
  }, [loadApp]);

  function toastTimeout(
    showToastValue: boolean,
    toastType: "success" | "error",
    toastMessage: string | string[],
    heading?: string
  ) {
    if (!showToastValue) return;

    // Show toast via react-toastify
    Toast(
      toastType,
      toastMessage,
      heading || (toastType === "success" ? "Success" : "Error")
    );
  }

  async function sendOtp(purpose: string) {
    try {
      const res = await sendAuthOtp(callApi, purpose);
      const { relay_id } = res;

      setRelayId(relay_id);
      return relay_id;
    } catch (err) {
      toastTimeout(true, "error", (err as Error).message);
    }
  }

  async function requestOtpIfNeeded(purpose: string) {
    if (zpinExists || !zpinCheckComplete) return;
    await sendOtp(purpose);
  }

  function openOtpModal(
    setToggle: (value: boolean) => void,
    purpose: string
  ) {
    setOtpError(null);
    setRelayId("");
    requestOtpIfNeeded(purpose);
    setToggle(true);
  }

  async function handleEnablingAPIKeyWithZpin(
    zpin: string,
    otp: string,
    setInvalidOTPError: any
  ) {
    setOtpVerificationLoading(true);
    try {
      if (!zpin && !otp) {
        setOtpVerificationLoading(false);
        return;
      }
      const res = await updateKeyStatus(currentKeyName, AppKeyStatus.ENABLED, {
        zpin,
        otp,
        relay_id: relayId,
      });
      setOtpVerificationLoading(false);
      setToggleResetOtp(false);
      setToggleOtpVerificationForEnableAPIKey(false);
      setApiKeyCardLoading(true);
      setApp((app: any) => ({
        ...app,
        ...(res?.keys?.length > 0 && {
          keys: res.keys,
        }),
      }));
      setApiKeyCardLoading(false);
    } catch (err: any) {
      setOtpVerificationLoading(false);
      const errorMessage = err?.message || err?.response?.data?.err || "";
      setOtpError(errorMessage);
      setInvalidOTPError(errorMessage);
    }
  }

  async function handleDeactivateKeyWithZpin(
    zpin: string,
    otp: string,
    setInvalidOTPError: any
  ) {
    setOtpVerificationLoading(true);
    try {
      if (!zpin && !otp) {
        setOtpVerificationLoading(false);
        return;
      }
      const res = await updateKeyStatus(currentKeyName, AppKeyStatus.DISABLED, {
        zpin,
        otp,
        relay_id: relayId,
      });
      setOtpVerificationLoading(false);
      setToggleResetOtp(false);
      setToggleOtpVerificationForDisableAPIKey(false);
      setApiKeyCardLoading(true);
      setApp((app: any) => ({
        ...app,
        ...(res?.keys?.length > 0 && {
          keys: res.keys,
        }),
      }));
      setApiKeyCardLoading(false);
    } catch (err: any) {
      setOtpVerificationLoading(false);
      const errorMessage = err?.message || err?.response?.data?.err || "";
      setOtpError(errorMessage);
      setInvalidOTPError(errorMessage);
    }
  }

  async function handleAddServiceWithZpin(
    enabledAppServices: any,
    zpin: string,
    otp: string,
    setInvalidOTPError: any
  ) {
    const arrayOfIds = enabledAppServices.map((service: any) => productPID(service));
    setOtpVerificationLoading(true);
    if (!zpin && !otp) return;

    try {
      const payload: Record<string, unknown> = {
        products_access: arrayOfIds,
      };
      if (zpin) payload.zpin = zpin;
      if (otp) {
        payload.otp = otp;
        payload.relay_id = relayId;
      }
      await callApi(`/apps/${app?.app_id}`, {
        method: "PUT",
        data: payload,
      });
      const res = await fetchAppWithKeys(app?.app_id as string);
      setOtpVerificationLoading(false);
      setToggleResetOtp(false);
      setToggleOtpVerificationForAddServiceToApp(false);
      setCardLoading(true);
      setApp((currentApp: any) => ({
        ...currentApp,
        products_access: res.products_access,
        ...(res?.keys?.length > 0 && {
          keys: res.keys,
        }),
      }));
      setCardLoading(false);
    } catch (err: any) {
      setOtpVerificationLoading(false);
      const errorMessage = err?.message || err?.response?.data?.err || "";
      const namedMessage = replaceProductIdsWithNames(errorMessage, [
        ...ensureArray(enabledAppServices),
        ...ensureArray(app?.products_access),
      ]);
      if (isZpinOrOtpError(errorMessage)) {
        setOtpError(namedMessage);
        setInvalidOTPError(namedMessage);
        return;
      }
      setOtpError(null);
      setToggleOtpVerificationForAddServiceToApp(false);
      toastTimeout(
        true,
        "error",
        namedMessage || "Unable to update app services",
        "Unable to update services"
      );
    }

    return;
  }

  function handleDeleteKey(value: any) {
    setApp((app: any) => ({
      ...app,
      keys: value,
    }));
    setToggleDeleteKeyPopUp(false);
    return;
  }

  function handleDeleteKeyCancel() {
    setToggleDeleteKeyPopUp(false);
    return;
  }

  function handleDeleteAuthCancel() {
    setToggleDeleteAppAuthPopUp(false);
    return;
  }

  function checkKeyStatus() {
    const statusArray: any = [];
    if (app?.keys?.length) {
      app?.keys?.map((key: any) => {
        const { status } = key;
        statusArray.push(status);
      });
      if (statusArray.includes(AppKeyStatus.ENABLED)) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  function handleAddAppKey(value: any) {
    setApp((app: any) => ({
      ...app,
      keys: value,
    }));

    setToggleAddAppKey(false);
    return;
  }
  useTitle("Zoop Customer Platform | App Info ");

  const tableHeaders = [
    {
      name: "Key name",
      className: Styles.headerContainer,
    },
    {
      name: "Created At",
      className: Styles.headerContainer,
    },
    {
      name: "Updated At",
      className: Styles.headerContainer,
    },
    {
      name: "Status",
      className: Styles.headerContainer,
    },
    {
      name: "",
      className: Styles.headerContainer + " " + Styles.deleteColumn,
    },
  ];

  return (
    <div className={`${Styles.containerApp}`}>
      {loading ? (
        <div
          style={{
            height: "100vh",
          }}
          className="d-flex align-items-center justify-content-center"
        >
          <Loading loadingColor={ACCENTCOLOR} />
        </div>
      ) : (
        <div
          style={{ height: "100%", minHeight: "100vh" }}
          className="container-fluid px-4"
        >
          <div className="d-flex justify-content-between align-items-center align-items-center">
            <div className="py-2">
              <Info
                message={app?.test_app ? app_message.TEST : app_message.LIVE}
              />
            </div>
            <div className="d-flex align-items-center">
              <div className={"ml-auto px-4 "}>
                {app?.products_access?.length === 0 ? (
                  <RenderTooltip
                    placement="bottom"
                    title="Add products to this app to enable settings"
                  >
                    <div
                      className={`${Styles.appSettings}`}
                      style={{ cursor: "no-drop", opacity: 0.5 }}
                    >
                      App Settings
                    </div>
                  </RenderTooltip>
                ) : (
                  <div
                    {...testIdProps("manage-apps-app-settings")}
                    onClick={() => {
                      history(`/application/manage-apps/${app?.app_id}/settings`, {
                        state: {
                          id: app?.app_id,
                          // ips: app?.ip_whitelisted,
                          // services: app?.service_access,
                        },
                      });
                    }}
                    className={`${Styles.primaryLink}`}
                  >
                    App Settings
                  </div>
                )}
              </div>
              <div className="pl-1 pr-1">
                {app?.products_access?.length === 0 ? (
                  <RenderTooltip
                    placement="bottom"
                    title="Add products to this app to Add an API key"
                  >
                    <div
                      style={primaryButtonStyle}
                      className={`d-flex align-items-center justify-content-center ${Styles.btn}`}
                    >
                      <p className={`mb-0 ${Styles.btnText}`}>Add Key</p>
                    </div>
                  </RenderTooltip>
                ) : (
                  <StyledButton
                    onClick={() => setToggleAddAppKey(true)}
                    id="manage-apps-add-key"
                    style={{
                      ...buttonStyles.primary.normal,
                      width: "130px",
                      marginLeft: "20px",
                    }}
                    hoverStyle={{
                      ...buttonStyles.primary.hover,
                      width: "130px",
                      marginLeft: "20px",
                    }}
                  >
                    Add Key
                  </StyledButton>
                )}
              </div>
              {app?.test_app && (
                <StyledButton
                  onClick={() => navigate("/application/trial-center")}
                  id="manage-apps-trial"
                  style={{
                    ...buttonStyles.primary.normal,
                    width: "130px",
                    marginLeft: "20px",
                  }}
                  hoverStyle={{
                    ...buttonStyles.primary.hover,
                    width: "130px",
                    marginLeft: "20px",
                  }}
                >
                  Trial
                </StyledButton>
              )}
            </div>
          </div>

          <div className="">
            <div className="row mt-2">
              <div className="col-lg-4">
                <div
                  className={`${Styles.card} ${Styles.cardAnimation} pl-2 pb-3`}
                >
                  <div className={`p-3 ${Styles.cardHeader}`}>
                    <p className={"mb-0 px-2 py-2 card_title_bold_text"}>
                      App Details
                    </p>
                  </div>
                  <div className="px-3 pb-3">
                    <div className="d-flex align-items-start py-2 mt-2">
                      <div className={`${Styles.keyWidth} px-2`}>
                        <p className={`mb-0 ${Styles.key}`}>App ID:</p>
                      </div>
                      <div className={`px-2 ${Styles.valueWidth}`}>
                        <div className="d-flex align-items-center gap-2">
                          <p
                            className={`mb-0 ${Styles.value} ${Styles.appIdValue}`}
                            title={app?.app_id}
                          >
                            {app?.app_id}
                          </p>
                          {app?.app_id && !copiedAppId && (
                            <span
                              {...testIdProps("manage-apps-copy-app-id")}
                              style={{ display: "inline-flex", cursor: "pointer" }}
                            >
                              <CopySvg
                                style={{
                                  width: "14px",
                                  height: "14px",
                                  flexShrink: 0,
                                }}
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(
                                      app.app_id
                                    );
                                    setCopiedAppId(true);
                                    setTimeout(() => setCopiedAppId(false), 2000);
                                  } catch (err) {
                                    console.error("Failed to copy App ID:", err);
                                  }
                                }}
                              />
                            </span>
                          )}
                          {copiedAppId && (
                            <span
                              style={{ fontSize: "10px", color: "green" }}
                            >
                              Copied!
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-start py-2 mt-2">
                      <div className={`${Styles.keyWidth} px-2`}>
                        <p className={`mb-0 ${Styles.key}`}>Name:</p>
                      </div>
                      <div className={`px-2 ${Styles.valueWidth}`}>
                        <p
                          className={`mb-0 ${Styles.value} ${Styles.appIdValue}`}
                          title={app?.app_name}
                        >
                          {app?.app_name}
                        </p>
                      </div>
                    </div>
                    <div className="d-flex align-items-start py-2 mt-2">
                      <div className={`${Styles.keyWidth} px-2`}>
                        <p className={`mb-0 ${Styles.key}`}>Created At:</p>
                      </div>
                      <div className={`px-2 ${Styles.valueWidth}`}>
                        <p className={`mb-0 ${Styles.value}`}>
                          {
                            formatApiDate(app?.created_at)
                          }
                        </p>
                      </div>
                    </div>
                    <div className="d-flex align-items-start py-2 mt-2">
                      <div className={`${Styles.keyWidth} px-2`}>
                        <p className={`mb-0 ${Styles.key}`}>Keys:</p>
                      </div>
                      <div className={`px-2 ${Styles.valueWidth}`}>
                        <p className={`mb-0 ${Styles.value}`}>
                          {app?.keys?.length}
                        </p>
                      </div>
                    </div>
                    <div className="d-flex align-items-start py-2 mt-2">
                      <div className={`${Styles.keyWidth} px-2`}>
                        <p className={`mb-0 ${Styles.key}`}>Products:</p>
                      </div>
                      <div className={`px-2 ${Styles.valueWidth}`}>
                        <p className={`mb-0 ${Styles.value}`}>
                          {app?.products_access?.length}
                        </p>
                      </div>
                    </div>
                    {!app?.test_app && (
                      <div className="d-flex justify-content-end">
                        <div
                          style={{ cursor: "pointer" }}
                          {...testIdProps("manage-apps-delete-app")}
                          onClick={() => {
                            if (checkKeyStatus()) {
                              toastTimeout(
                                true,
                                "error",
                                "Disable all API keys before deleting the app."
                              );
                            } else {
                              setToggleDeleteAppAuthPopUp(true);
                            }
                          }}
                        >
                          <Delete className={Styles.redDeleteIcon} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-lg-8">
                {cardLoading ? (
                  <div
                    className={`d-flex align--items-center justify-content-center ${Styles.loadingCard}`}
                  >
                    <Loading loadingColor={ACCENTCOLOR} />
                  </div>
                ) : (
                  <div
                    className={`${Styles.card} ${Styles.cardAnimation} pb-3`}
                  >
                    <div
                      className={`pb-1 pt-3 pr-3 pl-3 ${Styles.cardHeader} ${Styles.productLevelSetting}`}
                    >
                      <div className="d-flex align-items-center justify-content-between px-3">
                        <p className={"mb-0 px-2 py-2 card_title_bold_text"}>
                          Product Level Settings
                        </p>
                        <div className="ml-auto px-2">
                          <div
                            className={Styles.primaryLink}
                            {...testIdProps("manage-apps-manage-products")}
                            onClick={() => setShowAddService(true)}
                          >
                            Manage Products
                          </div>
                        </div>
                      </div>
                    </div>
                    {app?.products_access?.length === 0 ? (
                      <div
                        className={`${Styles.emptyStateDiv} d-flex align-items-center justify-content-center`}
                      >
                        <div className={Styles.noProductContainer}>
                          <img
                            src={EmptyService}
                            alt="no_service_found"
                            className={Styles.emptyStateIllustration}
                          />
                          <p className={`mb-1 pt-2 overline_strong_text`}>
                            No products associated
                          </p>
                          <p className="overline_text">
                            Link a product to get started.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="d-flex align-items-center flex-wrap p-3"
                        style={{
                          maxHeight: "250px",
                          overflow: "auto",
                        }}
                      >
                        {app?.products_access?.map((service: any) => {
                          return (
                            <div className={"p-1"} key={productPID(service)}>
                              <div
                                className={`${Styles.addedService} px-3 py-2`}
                              >
                                <p className={`mb-0 ${Styles.serviceName}`}>
                                  {service.name}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="row mt-3">
              <DataTable
                headers={tableHeaders}
                loading={apiKeyCardLoading}
                rows={app?.keys}
                headerTitle={"Manage Keys"}
                pagination={false}
                purpose=""
                headerClass={Styles.headerContainer}
                tableHeader={
                  <p
                    className={`mb-0 py-2 card_title_bold_text ${Styles.headerTitle}`}
                  >
                    {"Manage Keys"}
                  </p>
                }
                emptyState={
                  <div className={Styles.noProductContainer}>
                    <img
                      src={emptyStateKey}
                      alt="no_service_found"
                      className={Styles.emptyStateIllustration}
                    />
                    <p className={`mb-1 pt-2 overline_strong_text`}>
                      No active key associated
                    </p>
                    <p className="overline_text">
                      Click Add Key to generate a new API key...
                    </p>
                  </div>
                }
              >
                {app?.keys?.map((key: any, index: number) => {
                  return (
                    <tr
                      key={`${key.key_name} ${key.key_id}`}
                      id={`table-row-${index}-${key.key_name
                        .split(" ")
                        .join("-")}`}
                      className={Styles.tableRow}
                    >
                      <td className={`${Styles.tableBodyText}`}>
                        {key.key_name}
                      </td>
                      <td className={`${Styles.tableBodyText}`}>
                        {formatApiDate(key.created_at)}
                      </td>
                      <td className={`${Styles.tableBodyText}`}>
                        {formatApiDate(key.updated_at)}
                      </td>
                      <td className={`${Styles.tableBodyText}`}>
                        <div className="d-flex align-items-center">
                          <Switch
                            positiveLabel="Enable"
                            negativeLabel="Disable"
                            id={`manage-apps-api-key-switch-${index}`}
                            checked={key.status === AppKeyStatus.ENABLED}
                            onChange={() => {
                              setCurrentKeyName(key.key_name);
                              if (key.status === AppKeyStatus.ENABLED) {
                                openOtpModal(
                                  setToggleOtpVerificationForDisableAPIKey,
                                  OTP_PURPOSE.MANAGING_APP_KEYS
                                );
                                return;
                              }
                              openOtpModal(
                                setToggleOtpVerificationForEnableAPIKey,
                                OTP_PURPOSE.MANAGING_APP_KEYS
                              );
                            }}
                          ></Switch>
                        </div>
                      </td>
                      <td
                        className={`${Styles.tableBodyText} ${Styles.deleteColumn} text-center`}
                      >
                        <div
                          id="app-info-delete-key-1"
                          className={Styles.showWhiteDeleteIcon}
                        >
                          <Delete
                            id="delete-button-app-info"
                            className={Styles.whiteDeleteIcon}
                          />
                        </div>
                        <div
                          className={Styles.showRedDeleteIcon}
                          {...testIdProps(`manage-apps-delete-api-key-${index}`)}
                          onClick={() => {
                            setCurrentKeyName(key.key_name);
                            if (key.status === AppKeyStatus.ENABLED) {
                              toastTimeout(
                                true,
                                "error",
                                "Disable the key before deleting one."
                              );
                              return;
                            }
                            setToggleDeleteKeyPopUp(true);
                          }}
                        >
                          <Delete className={Styles.redDeleteIcon} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </DataTable>
            </div>
          </div>
        </div>
      )}
      {toggleDeleteKeyPopUp ? (
        <DeleteApiKey
          onSubmit={(value: any) => handleDeleteKey(value)}
          onCancel={handleDeleteKeyCancel}
          keyName={currentKeyName}
          appId={app?.app_id}
        />
      ) : null}
      {toggleDeleteAppAuthPopUp ? (
        <DeleteApp onCancel={handleDeleteAuthCancel} appId={app?.app_id} />
      ) : null}
      {toggleAddAppKey ? (
        <AddApiKey
          onSubmit={(value: any) => handleAddAppKey(value)}
          onCancel={() => setToggleAddAppKey(false)}
          appId={app?.app_id}
        />
      ) : null}
      {showAddService ? (
        <AddServiceModal
          enabledService={app?.products_access}
          onSubmit={(enabledAppServices: any) => {
            setUpdatedServices(enabledAppServices);
            setShowAddService(false);
            openOtpModal(
              setToggleOtpVerificationForAddServiceToApp,
              OTP_PURPOSE.MANAGING_SERVICES
            );
          }}
          onCancel={() => setShowAddService(false)}
          forbiddenServices={forbiddenServices}
        />
      ) : null}
      {toggleOtpVerificationForAddServiceToApp && (
        <ZpinMainPopup
          error={otpError}
          setError={(value: any) => setOtpError(value)}
          zpinExists={zpinExists}
          toggleResetOtp={toggleResetOtp}
          setToggleResetOtp={(value: boolean) => setToggleResetOtp(value)}
          resendOtp={(purpose: string = OTP_PURPOSE.MANAGING_SERVICES) =>
            sendOtp(purpose)
          }
          relayId={relayId}
          isLoading={otpVerificationLoading}
          purpose={OTP_PURPOSE.MANAGING_SERVICES}
          onVerifyOtp={(zpin: string, otp: string, setInvalidOtpError: any) => {
            handleAddServiceWithZpin(
              updatedServices,
              zpin,
              otp,
              setInvalidOtpError
            );
          }}
          onCancel={() => {
            setOtpError(null);
            setRelayId("");
            setToggleOtpVerificationForAddServiceToApp(false);
          }}
          updateZpinStatus={() => setZpinExists(true)}
          handleZpinAuth={(
            zpin: string,
            otp: string,
            setInvalidZpinError: any
          ) =>
            handleAddServiceWithZpin(
              updatedServices,
              zpin,
              otp,
              setInvalidZpinError
            )
          }
        />
      )}
      {toggleOtpVerificationForDisableAPIKey && (
        <ZpinMainPopup
          error={otpError}
          setError={(value: any) => setOtpError(value)}
          zpinExists={zpinExists}
          toggleResetOtp={toggleResetOtp}
          relayId={relayId}
          setToggleResetOtp={(value: boolean) => setToggleResetOtp(value)}
          resendOtp={(purpose: string = OTP_PURPOSE.MANAGING_APP_KEYS) =>
            sendOtp(purpose)
          }
          isLoading={otpVerificationLoading}
          purpose={OTP_PURPOSE.MANAGING_APP_KEYS}
          onVerifyOtp={(zpin: string, otp: string, setInvalidOTPError: any) => {
            handleDeactivateKeyWithZpin(zpin, otp, setInvalidOTPError);
          }}
          onCancel={() => {
            setOtpError(null);
            setRelayId("");
            setToggleOtpVerificationForDisableAPIKey(false);
          }}
          handleZpinAuth={handleDeactivateKeyWithZpin}
          updateZpinStatus={() => setZpinExists(true)}
        />
      )}
      {toggleOtpVerificationForEnableAPIKey && (
        <ZpinMainPopup
          error={otpError}
          setError={(value: any) => setOtpError(value)}
          zpinExists={zpinExists}
          toggleResetOtp={toggleResetOtp}
          relayId={relayId}
          setToggleResetOtp={(value: boolean) => setToggleResetOtp(value)}
          resendOtp={(purpose: string = OTP_PURPOSE.MANAGING_APP_KEYS) =>
            sendOtp(purpose)
          }
          isLoading={otpVerificationLoading}
          purpose={OTP_PURPOSE.MANAGING_APP_KEYS}
          onVerifyOtp={(zpin: string, otp: string, setInvalidOTPError: any) => {
            handleEnablingAPIKeyWithZpin(zpin, otp, setInvalidOTPError);
          }}
          onCancel={() => {
            setOtpError(null);
            setRelayId("");
            setToggleOtpVerificationForEnableAPIKey(false);
          }}
          handleZpinAuth={handleEnablingAPIKeyWithZpin}
          updateZpinStatus={() => setZpinExists(true)}
        />
      )}
    </div>
  );
}

export default AppInfo;
